import type { ReactNode } from 'react';
import { createColumnHelper, tableFeatures, useTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export type Direcao = 'asc' | 'desc';

/** Coluna: `campo` ordena no servidor (omitido = coluna sem ordenacao). */
export interface ColunaMargem<T> {
  id: string;
  rotulo: string;
  campo?: string;
  direita?: boolean;
  mono?: boolean;
  celula: (linha: T) => ReactNode;
}

export interface TabelaOrdenavelProps<T> {
  legenda: string;
  linhas: T[];
  colunas: ColunaMargem<T>[];
  ordenar: string;
  direcao: Direcao;
  /** Cabecalho clicado: a ordem roda na API. */
  onOrdenar: (campo: string) => void;
  chave: (linha: T) => string;
  testIdLinha: string;
}

const features = tableFeatures({});

/** Tabela densa (TanStack Table) com ordenacao manual: so dispara `onOrdenar`; a ordem das linhas e a da API. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function TabelaOrdenavel<T extends Record<string, any>>({ legenda, linhas, colunas, ordenar, direcao, onOrdenar, chave, testIdLinha }: TabelaOrdenavelProps<T>) {
  const col = createColumnHelper<typeof features, T>();
  const columns = colunas.map((c) =>
    col.display({
      id: c.id,
      header: () =>
        c.campo ? (
          <button
            type="button"
            onClick={() => onOrdenar(c.campo!)}
            aria-label={`Ordenar por ${c.rotulo}`}
            className={cn('inline-flex items-center gap-1 font-medium hover:text-ink', c.direita && 'flex-row-reverse')}
          >
            {c.rotulo}
            {ordenar === c.campo ? (
              direcao === 'asc' ? <ArrowUp aria-hidden="true" className="size-3.5" /> : <ArrowDown aria-hidden="true" className="size-3.5" />
            ) : (
              <ChevronsUpDown aria-hidden="true" className="size-3.5 text-ink-disabled" />
            )}
          </button>
        ) : (
          c.rotulo
        ),
      cell: ({ row }) => c.celula(row.original),
    }),
  );
  const table = useTable({ features, columns, data: linhas, getRowId: chave });
  const porId = new Map(colunas.map((c) => [c.id, c]));
  const ariaSort = (id: string): 'ascending' | 'descending' | 'none' | undefined => {
    const c = porId.get(id);
    if (!c?.campo) return undefined;
    return c.campo === ordenar ? (direcao === 'asc' ? 'ascending' : 'descending') : 'none';
  };

  return (
    <div className="overflow-x-auto">
      <table aria-label={legenda} className="w-full min-w-[720px] border-collapse text-small">
        <thead>
          {table.getHeaderGroups().map((g) => (
            <tr key={g.id} className="text-left text-caption text-ink-muted">
              {g.headers.map((h) => (
                <th key={h.id} scope="col" aria-sort={ariaSort(h.column.id)} className={cn('px-3 py-2.5 font-medium first:pl-0 last:pr-0', porId.get(h.column.id)?.direita && 'text-right')}>
                  <table.FlexRender header={h} />
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} data-testid={testIdLinha} className="border-t border-line-soft">
              {row.getAllCells().map((cell) => {
                const c = porId.get(cell.column.id);
                return (
                  <td key={cell.id} className={cn('px-3 py-2.5 first:pl-0 last:pr-0', c?.direita && 'text-right', c?.mono && 'tabular')}>
                    <table.FlexRender cell={cell} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
